import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Overview = () => (
  <>
    <Card gap='$4'>
      <DocSection id='what-is-a-guild' title='What is a Guild?'>
        <Paragraph size='$5'>
          A Guild is your community's home on Guild — the central hub where your{' '}
          <Link href='/docs/events/overview'>Events</Link>,{' '}
          <Link href='/docs/presentations/overview'>Presentations</Link>, and{' '}
          <Link href='/docs/guilds/members'>Members</Link> all come together.
          Whether you're running a local tech meetup, a hobby group, a
          professional network, or a developer community, a Guild gives you a
          dedicated space to organize and grow.
        </Paragraph>
        <Paragraph size='$5'>
          Every Guild has its own profile page with a unique URL that you can
          share with anyone. Visitors can see what your community is about, browse
          upcoming Events, explore recorded Presentations, and meet the Members
          who make it all happen.
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-profile-page.png'
          alt='Toronto JavaScript guild profile page showing cover photo, profile photo, description, member count, upcoming events, and tab navigation'
          caption='A Guild profile page — the home for your community'
        />
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='what-guilds-include' title='What Every Guild Includes'>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='home' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Profile page</Paragraph> — A
              customizable page with your Guild's cover photo, profile photo, name,
              description, and Member count. This is the first thing visitors see and
              the main entry point for your community. See{' '}
              <Link href='/docs/guilds/profile'>Guild Profile Page</Link> for details.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Events</Paragraph> — Create and
              manage in-person or online Events with RSVPs, waitlists, ticketing, and
              venue details. Your Guild's Events tab gives visitors a full view of
              what's coming up and what's already happened. See{' '}
              <Link href='/docs/events/overview'>Events</Link> to learn more.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Presentations</Paragraph> — Share
              recorded talks, slide decks, and session content from your Events. Your
              Guild's Presentations tab creates a permanent library of your
              community's knowledge. See{' '}
              <Link href='/docs/presentations/overview'>Presentations</Link> to learn
              more.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='people' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Members</Paragraph> — Manage your
              community with roles and permissions. Organizers run the Guild, Editors
              help manage content, and Members participate in Events. See{' '}
              <Link href='/docs/guilds/members'>Managing Members</Link> for the full
              breakdown.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Integrations</Paragraph> — Connect
              your Guild to{' '}
              <Link href='/docs/guilds/bluesky'>Bluesky</Link> for automatic Event
              announcements,{' '}
              <Link href='/docs/guilds/google-calendar'>Google Calendar</Link> for
              syncing Events, and{' '}
              <Link href='/docs/guilds/calendar-feeds'>Calendar Feeds</Link> for
              subscribing from any calendar app.
            </Paragraph>
          </XStack>
        </YStack>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='public-vs-approval' title='Public and Approval-Based Guilds'>
        <Paragraph size='$5'>
          By default, Guilds are public — anyone can join with a single click on
          the Join button. This is ideal for open communities that want to grow
          quickly and welcome everyone.
        </Paragraph>
        <Paragraph size='$5'>
          For communities that need more control over who joins, Organizers can
          set up a{' '}
          <Link href='/docs/guilds/forms'>Membership Form</Link> that prospective
          Members fill out when requesting to join. Organizers can then review
          applications and approve or deny each one individually. This is useful
          for professional networks, invite-only groups, or communities that want
          to collect information about their Members upfront.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='getting-started' title='Ready to Get Started?'>
        <Paragraph size='$5'>
          Creating a Guild takes just a few minutes. Head over to{' '}
          <Link href='/docs/guilds/creation'>Creating a Guild</Link> for a
          step-by-step guide, or explore the{' '}
          <Link href='/docs/guilds/settings'>Guild Settings</Link> page to see
          everything you can customize.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default Overview
